// The public onsite field report, served by a submission link token with no
// sign-in. Offices bookmark their link and file the onsites the National
// Accounts team never got a call about; each submission lands in the onsite
// console as a Completed job attributed to the link's franchisee.
//
// Standalone page: no app shell, no navigation, nothing that assumes a
// session. The layout is the sign-in pattern with a wider card.

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getPublicForm, submitPublicForm } from '../api.js'
import { useApiForm, blankToNull } from '../useApiForm.js'
import { Button, DateField, Input, Notice, Panel, Select } from '../components/ui/index.js'
import { formatDate, SEPARATOR } from '../vocab.js'
import { fieldFactory } from '../formField.jsx'

const EMPTY = {
  reportedByName: '',
  reportedByEmail: '',
  customerId: '',
  serviceDate: '',
  actualDonorCount: '',
  actualNoShowCount: '',
  collectorCount: '',
  mileageMiles: '',
  hours: '',
  tolls: '',
  parkingFees: '',
  notes: '',
}

function toPayload(form) {
  const payload = blankToNull(form)
  payload.reportedByName = form.reportedByName
  payload.reportedByEmail = form.reportedByEmail
  payload.customerId = form.customerId
  payload.serviceDate = form.serviceDate
  payload.actualDonorCount = form.actualDonorCount
  return payload
}

export default function PublicOnsiteReport() {
  const { token } = useParams()
  const [context, setContext] = useState(null)
  const [dead, setDead] = useState(false)
  const [done, setDone] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const { saving, error, fieldErrors, submit } = useApiForm()

  useEffect(() => {
    // A revoked link, a mistyped one and an unreachable server all read the
    // same to the office: this form is not available. Nothing to retry here.
    getPublicForm(token)
      .then((data) => setContext(data.form))
      .catch(() => setDead(true))
  }, [token])

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function onSubmit(event) {
    event.preventDefault()
    const result = await submit(() => submitPublicForm(token, toPayload(form)))
    if (result) {
      setDone(result)
      setForm(EMPTY)
      window.scrollTo(0, 0)
    }
  }

  const field = fieldFactory({ form, change, fieldErrors, idPrefix: 'pf-' })

  const shell = (children) => (
    <div className="public-wrap">
      <div className="public-kicker">Fastest Labs National Accounts</div>
      <h1>Onsite field report</h1>
      {children}
      <p className="muted public-note">
        Tracking and relationship reporting only. Nothing here posts to accounting, and no test
        results are collected.
      </p>
    </div>
  )

  if (dead) {
    return shell(
      <Notice tone="bad" className="public-gap">
        This link is not active. Ask the National Accounts team for a current submission link.
      </Notice>,
    )
  }
  if (!context) return shell(<div className="center-note">Loading</div>)

  if (done) {
    return shell(
      <>
        <Notice tone="ok" className="public-gap">
          Thank you. The onsite for {done.customerName} on {formatDate(done.serviceDate)} is
          recorded with the National Accounts team.
        </Notice>
        <div className="btnrow">
          <Button variant="primary" onClick={() => setDone(null)}>
            Submit another onsite
          </Button>
        </div>
      </>,
    )
  }

  return shell(
    <>
      <p className="muted public-intro">
        Ran an onsite for a National Account we did not dispatch? Record it here.
        {context.franchiseeName
          ? ` Filing as ${[context.franchiseeName, context.franchiseId]
              .filter(Boolean)
              .join(SEPARATOR)}.`
          : ''}
      </p>

      <div className="public-gap">
        <Panel>
          <form onSubmit={onSubmit} noValidate>
            {error ? <Notice tone="bad">{error}</Notice> : null}

            <div className="formgrid">
              {field('reportedByName', 'Your name')}
              {field('reportedByEmail', 'Email address', { type: 'email' })}

              <Select
                id="pf-customer"
                label="National Account customer"
                value={form.customerId}
                onChange={(customerId) => change('customerId', customerId)}
                error={fieldErrors.customerId}
                options={[
                  { value: '', label: 'Choose the customer' },
                  ...context.customers.map((customer) => ({
                    value: customer.id,
                    label: customer.name,
                  })),
                ]}
              />

              <DateField
                id="pf-serviceDate"
                label="Date of service"
                value={form.serviceDate}
                onChange={(serviceDate) => change('serviceDate', serviceDate)}
                error={fieldErrors.serviceDate}
              />

              {field('actualDonorCount', 'Number of donors', {
                type: 'number',
                step: '1',
                min: '0',
              })}
              {field('actualNoShowCount', 'No shows', {
                type: 'number',
                step: '1',
                min: '0',
                help: 'If any were scheduled and missed.',
              })}
              {field('collectorCount', 'Number of collectors on site', {
                type: 'number',
                step: '1',
                min: '0',
              })}
              {field('mileageMiles', 'Miles', { type: 'number', step: '0.01', min: '0' })}
              {field('hours', 'Hours', { type: 'number', step: '0.01', min: '0' })}
              {field('tolls', 'Toll fees', { type: 'number', step: '0.01', min: '0' })}
              {field('parkingFees', 'Parking fees', { type: 'number', step: '0.01', min: '0' })}

              <Input
                id="pf-notes"
                className="span-2"
                label="Additional notes"
                multiline
                value={form.notes}
                onChange={(event) => change('notes', event.target.value)}
              />
            </div>

            <div className="btnrow">
              <span className="spacer" />
              <Button
                type="submit"
                variant="primary"
                block
                disabled={
                  saving ||
                  !form.reportedByName.trim() ||
                  !form.reportedByEmail.trim() ||
                  !form.customerId ||
                  !form.serviceDate ||
                  form.actualDonorCount === ''
                }
              >
                {saving ? 'Submitting' : 'Submit onsite report'}
              </Button>
            </div>
          </form>
        </Panel>
      </div>
    </>,
  )
}
